import { inputManager } from '../../input/manager';
import { progressStore } from '../../store/progress';
import { settingsStore, TOLERANCE_MS, type TolerancePreset } from '../../store/settings';
import { InputCheck } from '../components/InputCheck';
import { InputPicker } from '../components/InputPicker';
import { useInputStatus, useSettings } from '../hooks/stores';

const TOLERANCE_LABELS: Record<TolerancePreset, string> = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  advanced: 'Avancé',
};

export function SettingsPage() {
  const settings = useSettings();
  const status = useInputStatus();

  return (
    <main className="page settings">
      <header className="page-head">
        <h1>Réglages</h1>
      </header>

      <section className="panel">
        <h2>Source d’entrée</h2>
        <InputPicker />
        <h3>Vérifier la détection</h3>
        <InputCheck />
      </section>

      <section className="panel">
        <h2>Micro</h2>
        <label className="field">
          <span>
            Compensation de latence : <strong>{settings.micLatencyMs} ms</strong>
          </span>
          <input
            type="range"
            min={0}
            max={250}
            step={5}
            value={settings.micLatencyMs}
            onChange={(e) => {
              const v = Number(e.target.value);
              settingsStore.set({ micLatencyMs: v });
              inputManager.setLatency(v);
            }}
          />
        </label>
        <p className="muted">
          Chaque attaque est datée à sa position dans le flux audio : le temps de reconnaissance de la note n’entre pas dans la mesure. Reste la latence d’entrée de l’appareil
          (micro, pilote, tampons du navigateur), typiquement 10 à 50 ms, que cette valeur retranche. En mode tempo imposé, si vos notes sont systématiquement jugées « en retard »
          alors que vous jouez en place, augmentez-la ; si elles sont jugées « en avance », diminuez-la. Le mode attente n’en dépend pas.
        </p>
        {status.kind === 'mic' && status.inputLatencyMs !== undefined && (
          <p className="muted">
            Latence d’entrée annoncée par le navigateur : {status.inputLatencyMs} ms.{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                const v = status.inputLatencyMs ?? 0;
                settingsStore.set({ micLatencyMs: v });
                inputManager.setLatency(v);
              }}
            >
              Utiliser cette valeur
            </button>
          </p>
        )}
        {status.kind === 'mic' && status.processing && (
          <p className="muted">
            Ce navigateur applique : annulation d’écho {String(status.processing.echoCancellation ?? 'inconnu')}, réduction de bruit {String(status.processing.noiseSuppression ?? 'inconnu')},
            gain automatique {String(status.processing.autoGainControl ?? 'inconnu')}.
          </p>
        )}
      </section>

      <section className="panel">
        <h2>Évaluation</h2>
        <fieldset className="field">
          <legend>Tolérance rythmique (mode tempo imposé)</legend>
          {(Object.keys(TOLERANCE_LABELS) as TolerancePreset[]).map((k) => (
            <label key={k} className="radio">
              <input type="radio" name="tolerance" checked={settings.tolerance === k} onChange={() => settingsStore.set({ tolerance: k })} />
              {TOLERANCE_LABELS[k]} · ± {TOLERANCE_MS[k]} ms
            </label>
          ))}
        </fieldset>
        <p className="muted">Au-delà de 300 ms, une note attendue est comptée manquée et une note jouée n’est plus rattachée au morceau.</p>
      </section>

      <section className="panel">
        <h2>Affichage</h2>
        <fieldset className="field">
          <legend>Noms des notes</legend>
          <label className="radio">
            <input type="radio" name="naming" checked={settings.naming === 'fr'} onChange={() => settingsStore.set({ naming: 'fr' })} />
            do ré mi fa sol la si
          </label>
          <label className="radio">
            <input type="radio" name="naming" checked={settings.naming === 'en'} onChange={() => settingsStore.set({ naming: 'en' })} />
            C D E F G A B
          </label>
        </fieldset>
        <label className="check">
          <input type="checkbox" checked={settings.showNames} onChange={(e) => settingsStore.set({ showNames: e.target.checked })} />
          Afficher les noms des notes sur les notes descendantes et le clavier
        </label>
        <label className="check">
          <input type="checkbox" checked={settings.showFingers} onChange={(e) => settingsStore.set({ showFingers: e.target.checked })} />
          Afficher les doigtés
        </label>
        <label className="check">
          <input type="checkbox" checked={settings.audioClick} onChange={(e) => settingsStore.set({ audioClick: e.target.checked })} />
          Clic audio du métronome (en mode micro, utilisez un casque : le clic est capté par le micro)
        </label>
      </section>

      <section className="panel">
        <h2>Outils de développement</h2>
        <label className="check">
          <input type="checkbox" checked={settings.developer} onChange={(e) => settingsStore.set({ developer: e.target.checked })} />
          Activer le mode développeur (source de synthèse, simulation de perte de connexion)
        </label>
        {settings.developer && (
          <div className="dev-tools">
            <p className="muted">
              La source « Synthèse » injecte des sons type piano dans la même chaîne d’analyse que le micro (worklet, sous-échantillonnage, détection). Elle sert à vérifier
              l’application sans piano : ce n’est pas un essai réel du micro. Avec cette source, les touches du clavier d’essai jouent une note de synthèse.
            </p>
            <div className="btn-row">
              <button type="button" className="btn small" onClick={() => inputManager.keyboard.simulateConnection('lost')}>
                Simuler une perte de connexion
              </button>
              <button type="button" className="btn small" onClick={() => inputManager.keyboard.simulateConnection('restored')}>
                Simuler le retour
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="panel">
        <h2>Données</h2>
        <p className="muted">La progression et les réglages sont enregistrés dans ce navigateur uniquement. Aucun compte, aucun envoi.</p>
        <button
          type="button"
          className="btn danger"
          onClick={() => {
            if (window.confirm('Effacer toute la progression enregistrée sur cet appareil ?')) progressStore.reset();
          }}
        >
          Effacer la progression
        </button>
      </section>
    </main>
  );
}
